import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Users, Calendar, DollarSign, Headphones, Check, ArrowRight, Award, MapPin } from "lucide-react";
import { useEffect } from "react";

const groupBenefits = [
  { icon: DollarSign, title: "Group Discounts", desc: "Special rates for groups of 10 or more travelers" },
  { icon: Calendar, title: "Flexible Scheduling", desc: "Choose dates that work for everyone" },
  { icon: Headphones, title: "Dedicated Coordinator", desc: "Personal group travel manager assigned" },
  { icon: Award, title: "Custom Activities", desc: "Group-specific experiences and bonding activities" },
];

const groupTypes = [
  {
    title: "Corporate Retreats",
    desc: "Team building, conferences, and corporate getaways",
    features: ["Meeting facilities", "Team activities", "Branded experiences"],
    image: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=600&q=80"
  },
  {
    title: "Family Reunions",
    desc: "Bringing families together for memorable celebrations",
    features: ["Multi-generational activities", "Connected accommodations", "Private dining"],
    image: "https://images.unsplash.com/photo-1609220136736-443140cffec6?w=600&q=80"
  },
  {
    title: "Friends Getaways",
    desc: "Adventure trips, milestone celebrations, and fun escapes",
    features: ["Bachelor/ette parties", "Adventure activities", "Nightlife options"],
    image: "https://images.unsplash.com/photo-1527631746610-bca00a040d60?w=600&q=80"
  },
];

export default function GroupTravelPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white" data-testid="group-travel-page">
      <SeoHead
        title="Group Travel Planning"
        description="Tripsyfy helps families, friends, communities, and groups coordinate flights, tours, and travel plans with human support."
        canonical="https://tripsyfy.com/services/group-travel"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 lg:pt-28 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-accent text-sm font-semibold tracking-wide uppercase mb-4">Our Services</p>
              <h1 className="heading-xl text-primary mb-6">Group Travel</h1>
              <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                Planning travel for a group? We specialize in coordinating seamless group experiences, from corporate retreats to family reunions and friend getaways.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {groupBenefits.map((benefit, i) => (
                <div key={benefit.title} className="bg-white p-6 rounded-lg border border-border text-center">
                  <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="font-bold text-primary mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Group Types */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="heading-lg text-primary mb-4">Group Travel Options</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We cater to all types of group travel with customized solutions for every occasion.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {groupTypes.map((type, i) => (
              <AnimatedSection key={type.title} delay={i * 0.1}>
                <div className="bg-white border border-border rounded-lg overflow-hidden hover:border-accent/30 transition-colors">
                  <div className="h-48 overflow-hidden">
                    <img src={type.image} alt={type.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-primary mb-1">{type.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{type.desc}</p>
                    <ul className="space-y-2">
                      {type.features.map((feature, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm">
                          <Check className="h-4 w-4 text-accent" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-primary mb-2">500+</p>
                <p className="text-sm text-muted-foreground">Groups Served</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">50+</p>
                <p className="text-sm text-muted-foreground">Destinations</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">10k+</p>
                <p className="text-sm text-muted-foreground">Happy Travelers</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">4.9★</p>
                <p className="text-sm text-muted-foreground">Average Rating</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-lg text-primary-foreground mb-4">Planning a Group Trip?</h2>
            <p className="text-primary-foreground/70 mb-8 max-w-xl mx-auto">
              Let us handle the logistics while you focus on creating memories with your group.
            </p>
            <Link to="/contact">
              <Button variant="navySolid" size="xl" className="gap-2">
                Get Group Quote <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
